// Static site data — product categories, credibility stats, factory info

export const KITCHEN_CONFIGURATIONS = [
  {
    name: "L-Shape",
    description: "Two adjacent walls carry all the storage and cooking zones. The most efficient layout for compact to mid-sized Indian kitchens.",
    diagram: [
      { x: 8, y: 45, w: 60, h: 12 },
      { x: 8, y: 15, w: 12, h: 30 },
    ],
  },
  {
    name: "U-Shape",
    description: "Three walls wrapped in modules — the most storage-dense layout. Ideal for families that cook multiple meals a day.",
    diagram: [
      { x: 8, y: 15, w: 12, h: 45 },
      { x: 8, y: 48, w: 84, h: 12 },
      { x: 80, y: 15, w: 12, h: 45 },
    ],
  },
  {
    name: "Straight",
    description: "A single continuous run — the cleanest silhouette. Perfect for apartment kitchens and narrow galley layouts.",
    diagram: [
      { x: 8, y: 40, w: 84, h: 14 },
    ],
  },
  {
    name: "Parallel",
    description: "Two runs facing each other with a walkway between. An efficient work triangle for two cooks working in parallel.",
    diagram: [
      { x: 8, y: 15, w: 84, h: 12 },
      { x: 8, y: 48, w: 84, h: 12 },
    ],
  },
  {
    name: "Island",
    description: "A signature layout with a central island — prep zone, breakfast counter, or hob. For homes where the kitchen doubles as a social space.",
    diagram: [
      { x: 8, y: 50, w: 84, h: 12 },
      { x: 30, y: 28, w: 40, h: 12 },
    ],
  },
];

export const KITCHEN_MATERIALS = {
  boards: ["BWR", "BWP", "HDHMR"],
  finishes: [
    { name: "Laminate", specs: "0.8mm · 1mm · 1.25mm" },
    { name: "Acrylic", specs: "1mm · 1.5mm" },
  ],
  hardware: ["Ebco", "Ozone", "Hettich", "Hafele"],
};

export const KITCHEN_GALLERY = [
  "/gallery-saurav-kitchen.png",
  "/gallery-kitchen-cream-wood.jpeg",
  "/gallery-kitchen-kalinga-blue.jpeg",
  "/gallery-kitchen-peach.jpeg",
  "/gallery-kitchen-sageblue.jpeg",
  "/gallery-sanjay.jpeg",
  "/gallery-arrah.jpeg",
  "/gallery-naresh2.jpeg",
  "/gallery-core.jpeg",
  "/gallery-ravi-pink.png",
  "/gallery-abha.png",
  "/gallery-vikash.png",
  "/gallery-sunil.jpg",
];

export const WARDROBE_SYSTEM_DIAGRAMS = [
  {
    name: "Sliding 2-Door",
    description: "Two overlapping panels on aluminium tracks — perfect where floor space is tight and swing clearance isn't available.",
    diagram: [
      { x: 6, y: 10, w: 44, h: 55 },
      { x: 50, y: 10, w: 44, h: 55, accent: true },
    ],
  },
  {
    name: "Sliding 3-Door",
    description: "Three panels for wider walls — 8–12 ft runs. Whisper-quiet tracks, laminate or mirror faces, no floor rails on premium builds.",
    diagram: [
      { x: 5, y: 10, w: 30, h: 55 },
      { x: 35, y: 10, w: 30, h: 55, accent: true },
      { x: 65, y: 10, w: 30, h: 55 },
    ],
  },
  {
    name: "Hinged Openable",
    description: "Traditional swing-door wardrobes with full-height access to every shelf — the most flexible layout for internal accessories.",
    diagram: [
      { x: 6, y: 8, w: 21, h: 59 },
      { x: 28, y: 8, w: 21, h: 59 },
      { x: 51, y: 8, w: 21, h: 59 },
      { x: 73, y: 8, w: 21, h: 59 },
    ],
  },
  {
    name: "Walk-In L-Shape",
    description: "Two runs of open shelving and hanging along adjacent walls — dresser unit, LED profile lighting, and an island are optional add-ons.",
    diagram: [
      { x: 8, y: 12, w: 84, h: 10 },
      { x: 8, y: 22, w: 10, h: 44 },
    ],
  },
];

export const WARDROBE_INTERIOR_HIGHLIGHTS = [
  {
    title: "Shelf Storage",
    detail: "Adjustable shelves with pin-fix supports — fold-and-stack for shirts, kurtas, sarees; anti-slip mats optional.",
  },
  {
    title: "Drawer Units",
    detail: "Standard drawers on soft-close runners — tandem drawers, jewellery inserts, cutlery-style trays, and lockable options available on request.",
  },
  {
    title: "Loft Units",
    detail: "Overhead cabinet modules that use the last two feet before the ceiling — for luggage, seasonal linen, and rarely-used items.",
  },
  {
    title: "Accessories",
    detail: "T Pullout, Trouser Rack, Accessory Drawer, Tie Pullout, Shoe Rack — engineered inserts that make everyday retrieval effortless.",
  },
];

export const WARDROBE_MATERIALS = {
  boards: ["BWR", "HDHMR"],
  finishes: [
    { name: "Laminate", specs: "0.8mm · 1mm · 1.25mm" },
    { name: "Acrylic", specs: "1mm · 1.5mm" },
  ],
  hardware: ["Hettich", "Ebco", "Ozone"],
};

export const WARDROBE_GALLERY = [
  "/wardrobe-glossy-white.png",
  "/wardrobe-rohit.jpg",
  "/wardrobe-nude.jpg",
  "/wardrobe-interior-open.png",
  "/wardrobe-apoorva-master.png",
  "/wardrobe-cream-sliding.png",
  "/wardrobe-sliding-open.png",
  "/wardrobe-shaker-white.png",
];

// ---------- OFFICE ----------
export const OFFICE_CATEGORIES = [
  {
    name: "Workstations",
    description: "Modular clusters for open floors — integrated cable trays, screen partitions, and add-on pedestals.",
    image: "/office-workstations.png",
  },
  {
    name: "Executive Desks",
    description: "Solid-core desks with veneer or laminate finishes — matched credenza, side returns, and lockable mobile pedestals.",
    image: "/office-executive.png",
  },
  {
    name: "Storage",
    description: "Sideboards and credenzas for the workplace — mix of hinged doors and drawers, keyed-alike or open configurations.",
    image: "/office-storage.png",
  },
  {
    name: "Filing Systems",
    description: "Full-height filing units with open display shelves and lockable base cabinets — for records, ring binders, and archive storage.",
    image: "/office-filing.png",
  },
  {
    name: "Reception Tables",
    description: "Custom reception desks — solid backs, cable-managed counters, LED-ready coves, and branded fascia options.",
    image: "/office-reception.png",
  },
  {
    name: "Conference Tables",
    description: "Boardroom-grade conference tables in veneer or laminate — flush power/data grommets, cable channels, and solid slab bases.",
    image: "/office-conference.png",
  },
];

export const OFFICE_BUILT_FOR_SCALE = [
  {
    title: "Standardised systems",
    detail: "Every module drawn to a repeatable BOM — a workstation in Bengaluru is identical to one in Pune.",
  },
  {
    title: "Durable for daily use",
    detail: "Commercial-grade edge banding, engineered structural 25mm tops, and hardware rated for continuous office use.",
  },
  {
    title: "Timely multi-city delivery",
    detail: "Batch production, flat-pack logistics, and trained installation crews coordinated across sites on a single schedule.",
  },
  {
    title: "Single point of contact",
    detail: "One account manager, one BOM, one delivery calendar — regardless of how many locations you're outfitting.",
  },
];

export const OFFICE_MATERIALS = {
  boards: ["HDHMR", "Pre-Laminated MDF", "Plywood"],
  finishes: [
    { name: "Laminate", specs: "0.8mm · 1mm · 1.25mm" },
    { name: "Veneer", specs: "Natural · Reconstituted" },
  ],
  hardware: [
    "Castor Wheels",
    "Keyboard Trays",
    "Cable Grommets",
    "Cam Locks",
  ],
};

// ---------- RESIDENTIAL ----------
export const RESIDENTIAL_CATEGORIES = [
  {
    name: "Shoe Racks & TV Units",
    description: "Foyer shoe cabinets and living-room TV consoles — the front-of-house pieces that combine display shelving with concealed daily storage.",
    image: "/res-tv-unit.png",
  },
  {
    name: "Beds & Bedside Tables",
    description: "Storage beds with hydraulic-lift bases and matched bedside units — soft-close drawers, pull-out trays, and USB-ready surfaces.",
    image: "/res-bed.png",
  },
  {
    name: "Dressing Tables",
    description: "Custom dressing units — LED-lit mirrors, jewellery inserts, drop-hinge trays, and pull-out compartments for the morning routine.",
    image: "/res-dressing.png",
  },
  {
    name: "Bookshelves",
    description: "Full-height and wall-mounted bookshelves — adjustable shelves, glass-fronted display sections, and LED profile lighting on request.",
    image: "/res-bookshelf.png",
  },
  {
    name: "Study Tables",
    description: "Study and work-from-home desks — cable-managed backs, drawer pedestals, and matching overhead or wall-mounted shelving.",
    image: "/res-study.png",
  },
  {
    name: "Chest of Drawers",
    description: "Standalone drawer chests for bedrooms and dressing corners — soft-close tandem runners, keyed-alike or key-different variants.",
    image: "/res-chest.png",
  },
];

export const RESIDENTIAL_GALLERY = [
  "/install-desk.jpeg",
  "/install-bed.jpeg",
  "/install-white-dressing.jpeg",
  "/install-wood-study.png",
  "/install-ayush-dresser.png",
  "/install-credenza.png",
  "/install-bookshelf.jpg",
  "/install-kids-study.png",
  "/install-dresser.png",
  "/install-study.jpg",
  "/install-mirror-cabinet.png",
  "/install-wine-bar.png",
];

// ---------- STANDALONE GALLERY ----------
// Extras exclusive to /gallery — add any bonus photos here that don't already
// live on the Kitchen / Wardrobe / Residential product pages.
// Each item: { src, category: "Kitchen"|"Wardrobe"|"Office"|"Residential" }
export const GALLERY_EXTRAS = [
  { src: "/gallery-pushkar-kitchen.png", category: "Kitchen" },
  { src: "/gallery-paramji-kitchen.png", category: "Kitchen" },
  { src: "/gallery-nitishji-kitchen.png", category: "Kitchen" },
  { src: "/gallery-dakbangla-kitchen.png", category: "Kitchen" },
  { src: "/gallery-devesh-kitchen.png", category: "Kitchen" },
  { src: "/gallery-sudha-kitchen.png", category: "Kitchen" },
  { src: "/gallery-stylewell-kitchen.png", category: "Kitchen" },
  { src: "/gallery-blue-marble-kitchen.png", category: "Kitchen" },
  { src: "/gallery-sage-wardrobe.png", category: "Wardrobe" },
  { src: "/gallery-ayush-wardrobe.png", category: "Wardrobe" },
  { src: "/gallery-vishnuji-wardrobe.png", category: "Wardrobe" },
  { src: "/gallery-muzz-wardrobe.png", category: "Wardrobe" },
  { src: "/gallery-ashok-wardrobe.png", category: "Wardrobe" },
  { src: "/gallery-pankaj-bed.png", category: "Residential" },
  { src: "/gallery-eshanvi-shoerack.png", category: "Residential" },
  { src: "/gallery-apoorva-crockery.png", category: "Residential" },
  { src: "/gallery-apoorva-partition.png", category: "Residential" },
];

// Real render vs. installed pairs from delivered projects
export const RENDER_VS_REALITY = [
  {
    label: "Sharika's Kitchen",
    render: "/sharika-render.jpg",
    reality: "/sharika-reality.jpeg",
  },
  {
    label: "Minni's Kitchen",
    render: "/minni-render.jpeg",
    reality: "/minni-reality.jpeg",
  },
  {
    label: "Sarita's Kitchen",
    render: "/sarita-render.jpeg",
    reality: "/sarita-reality.jpeg",
  },
  {
    label: "Ravi's Kitchen",
    render: "/ravi-render.jpg",
    reality: "/ravi-reality.jpeg",
  },
];

export const CATEGORIES = [
  {
    slug: "kitchen",
    name: "Modular Kitchens",
    tagline: "The Heart, Reimagined.",
    hero: "/hero-kitchen.webp",
    intro:
      "Precision-engineered kitchens built for Indian cooking rhythms — soft-close cabinetry, marine-grade ply carcasses, and finishes that survive daily use without losing their poise.",
  },
  {
    slug: "wardrobe",
    name: "Wardrobes",
    tagline: "Storage That Wears Well.",
    hero: "/hero-wardrobe.webp",
    intro:
      "Floor-to-ceiling wardrobes that turn walls into architecture. Sliding, hinged, or walk-in — engineered to your room, finished to your taste.",
  },
  {
    slug: "office",
    name: "Office Furniture",
    tagline: "Workspaces That Perform.",
    hero: "/hero-office.jpeg",
    intro:
      "Workstations, cabins, conference tables and storage — engineered for continuous use, wired for modern power/data, and delivered on schedule.",
  },
  {
    slug: "residential",
    name: "Residential Storage",
    tagline: "Room by Room, Considered.",
    hero: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1800&auto=format&fit=crop&q=80",
    intro:
      "TV units, crockery cabinets, study tables, foyers and utility storage — the pieces that hold everyday life together, made to last decades.",
  },
];

export const CATEGORY_BY_SLUG = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c]));

export const CREDIBILITY_STATS = [
  { number: "30", suffix: " days or less", label: "Standard turnaround from order to dispatch" },
  { number: "100+", suffix: "", label: "Projects delivered" },
  { number: "European", suffix: "", label: "Precision machinery driving every panel we produce" },
];

export const FACTORY_STATS = [
  { number: "2023", label: "Established in India" },
  { number: "7,000", label: "Sq. ft. manufacturing footprint" },
  { number: "15+", label: "Craftsmen, technicians & engineers" },
  { number: "12+", label: "Dealers / Partners & counting" },
];

export const WHO_WE_ARE = [
  {
    title: "Factory-led precision",
    body: "Every module — kitchen, wardrobe, workstation — is manufactured to spec inside our own factory. No outsourced job-work, no inconsistent finishes.",
  },
  {
    title: "Focused product lines",
    body: "Kitchens, wardrobes, storage, and workstations — the four categories we know deeply, refined over dozens of installations.",
  },
  {
    title: "After-sales that shows up",
    body: "A dedicated service team that responds fast — from a loose hinge tightening to a full replacement — because a great install is only the beginning of the relationship.",
  },
  {
    title: "Consistent at scale",
    body: "Same specifications, same materials, same tolerances — whether the module ships next door or a thousand kilometres away.",
  },
];

export const FOUNDERS = [
  {
    name: "Avantika Vohra",
    role: "Marketing",
    bio: "15+ yrs in Equities, Startups & Venture Capital. Ex-IIM Lucknow.",
    initials: "AV",
    tone: "navy",
    photo: null,
  },
  {
    name: "Shubhankar Watsa",
    role: "Production",
    bio: "15+ yrs in Real Estate & Portfolio Management.",
    initials: "SW",
    tone: "teal",
    photo: null,
  },
];

export const PROCESS_STEPS = [
  "Cold Press",
  "Precision Cut",
  "Grooving",
  "Edge Banding",
  "Multi Boring",
  "Assembly",
  "QC & Dispatch",
];

export const PARTNER_CATEGORIES = [
  "Franchisees / Dealers",
  "Builders",
  "Contractors",
  "Corporates",
  "Institutions",
  "Architects and Designers",
];

export const PARTNER_SPOKES = [
  { label: "Architects", angle: -90 },
  { label: "Interior Designers", angle: -30 },
  { label: "Corporate Offices", angle: 30 },
  { label: "Contractors", angle: 90 },
  { label: "Customers", angle: 210 },
];

export const DEALER_BENEFITS = [
  {
    title: "Reliable 30-Day Supply",
    body: "Your customer commitments matter. Our production-led operations honour delivery windows — measured, milled, delivered.",
  },
  {
    title: "Healthy Margins",
    body: "Direct-from-manufacturer pricing, dealer-only rate cards, and volume incentives designed to protect your bottom line.",
  },
  {
    title: "Marketing & Design Support",
    body: "Sample kits, showroom shells, catalogues, and design assistance for your architect and end-customer meetings.",
  },
  {
    title: "Territory Protection",
    body: "Defined dealership zones so your investment isn't undercut by another partner across the street.",
  },
];

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/kitchen", label: "Kitchen" },
  { to: "/wardrobe", label: "Wardrobe" },
  { to: "/residential", label: "Residential" },
  { to: "/office", label: "Office" },
  { to: "/gallery", label: "Gallery" },
  { to: "/become-a-dealer", label: "Become a Dealer" },
  { to: "/contact", label: "Contact" },
];

export const COMPANY = {
  name: "Tarush",
  legal: "Kawar Enterprise Private Limited",
  location: "Bangalore, India",
  address: "#29, Bloomingdale's Layout, Off Sarjapur Road, Bangalore - 560035",
  phone: "+91 99673 62695",
  email: "hello@tarushfurniture.com",
  established: "May 2023",
};
